import { v4 as uuid } from "uuid";
import {
  IOverlay,
  OverlayAnalogClockHoursNumbersTypeEnum,
  OverlayAnalogClockShapeEnum,
  OverlayClockFormatEnum,
  OverlayCountdownFormatEnum,
  OverlayCountdownStyleEnum,
  OverlayMarqueeBehaviorEnum,
  OverlayMarqueeHorizontalDirectionEnum,
  OverlayMarqueeTextPositionEnum,
  OverlayMarqueeVerticalDirectionEnum,
  OverlayStrokeTextFillTypeEnum,
  OverlayTypeEnum,
  OverlayVisualizerParticlesCenterShapeTypeEnum,
  OverlayVisualizerShapeTypeEnum,
} from "../../types/models";
import { IoMdClock, IoMdMusicalNotes } from "react-icons/io";
import { BiPencil } from "react-icons/bi";
import {
  MdAlbum,
  MdGif,
  MdImage,
  MdRectangle,
  MdShortText,
  MdVideoLibrary,
} from "react-icons/md";
import { FaCircle } from "react-icons/fa6";
import { TbRectangleRoundedTop } from "react-icons/tb";
import { BsCircle } from "react-icons/bs";
import { CgArrowTopRight } from "react-icons/cg";
import { PiClockCountdown } from "react-icons/pi";
import { TbClock12 } from "react-icons/tb";
import { RiTextSpacing } from "react-icons/ri";
import { TfiEye } from "react-icons/tfi";
import { IoTriangle } from "react-icons/io5";
import { SiAudiomack } from "react-icons/si";
import { OVERLAY_BLACK, OVERLAY_RED, OVERLAY_WHITE } from "./overlayColors.content";

export const CONTAINER_WIDTH = 600;
export const CONTAINER_HEIGHT = (CONTAINER_WIDTH / 16) * 9;
export const CONTAINER_DIAGONAL = Math.sqrt(
  CONTAINER_WIDTH * CONTAINER_WIDTH + CONTAINER_HEIGHT * CONTAINER_HEIGHT
);
export const IMAGE_PLACEHOLDER_URL = "/images/image_placeholder.jpeg";
export const GIF_PLACEHOLDER_URL = "/images/image_placeholder.jpeg";
export const VIDEO_PLACEHOLDER_URL = "/images/video_placeholder.png";

export const OVERLAY_TYPE = {
  TEXT: OverlayTypeEnum.text,
  SONG_TITLE: OverlayTypeEnum.song_title,
  SONG_AUTHOR: OverlayTypeEnum.song_author,
  SONG_TITLE_METADATA: OverlayTypeEnum.song_title_metadata,
  SONG_AUTHOR_METADATA: OverlayTypeEnum.song_author_metadata,
  SONG_ALBUM_METADATA: OverlayTypeEnum.song_album_metadata,
  IMAGE: OverlayTypeEnum.image,
  VIDEO: OverlayTypeEnum.video,
  GIF: OverlayTypeEnum.gif,
  CIRCLE: OverlayTypeEnum.circle,
  ARROW: OverlayTypeEnum.arrow,
  EDITABLE_TEXT: OverlayTypeEnum.complex_text,
  RECTANGLE: OverlayTypeEnum.rectangle,
  CUSTOM_RECTANGLE: OverlayTypeEnum.custom_rectangle,
  ELLIPSE: OverlayTypeEnum.ellipse,
  RING: OverlayTypeEnum.ring,
  COUNTDOWN: OverlayTypeEnum.countdown,
  DIGITAL_CLOCK: OverlayTypeEnum.digital_clock,
  ANALOG_CLOCK: OverlayTypeEnum.analog_clock,
  WEB_SOURCE: OverlayTypeEnum.web_source,
  PROGRESS: OverlayTypeEnum.progress,
  DONORS_CHATTERS_LIST: OverlayTypeEnum.donors_chatters_list,
  MARQUEE_TEXT: OverlayTypeEnum.marquee_text,
  TRIANGLE: OverlayTypeEnum.triangle,
  AUDIO_VISUALIZER: OverlayTypeEnum.audio_visualizer,
};

export const NEW_OVERLAY_OPTIONS = [
  {
    title: "Text",
    overlays_options: [
      {
        title: "Text",
        labelKey: "overlayTypes.text",
        icon: <MdShortText />,
        // icon: <AlignLeftOutlined />,
        type: OVERLAY_TYPE.TEXT,
        getTemplate: () => ({
          id: uuid(),
          type: "text",
          text: "Sample text",
          fontSize: 15,
          fill: OVERLAY_WHITE,
          top: 0,
          left: 0,
          settings: JSON.stringify({
            centralizedText: false,
            strokeTextStrokeEnabled: false,
            strokeTextFillType: OverlayStrokeTextFillTypeEnum.solid,
            strokeTextFillColorStops: [0, OVERLAY_WHITE],
            textFontFamily: "Arial",
          }),
        }),
      },
      {
        title: "Sliding Text",
        labelKey: "overlayTypes.slidingText",
        icon: <RiTextSpacing />,
        // icon: <ColumnWidthOutlined />,
        type: OVERLAY_TYPE.MARQUEE_TEXT,
        getTemplate: () => ({
          id: uuid(),
          type: OverlayTypeEnum.marquee_text,
          top: 0,
          left: 0,
          width: 100,
          height: 10,
          fill: "transparent",
          settings: JSON.stringify({
            marqueeTextFontSize: 2,
            marqueeTextFill: OVERLAY_WHITE,
            marqueeTextBold: true,
            marqueeText: "This is some scrolling text",
            marqueeTextHorizontalDirection:
              OverlayMarqueeHorizontalDirectionEnum.right_to_left,
            marqueeTextVerticalDirection:
              OverlayMarqueeVerticalDirectionEnum.up_to_down,
            marqueeTextBehavior: OverlayMarqueeBehaviorEnum.horizontal,
            marqueeTextDuration: 20,
            marqueeTextStrokeEnabled: false,
            marqueeTextStrokeFill: OVERLAY_WHITE,
            marqueeTextStrokeWidth: 2,
            marqueeOpacity: 1,
            marqueeStrokeOpacity: 1,
            marqueeTextPosition: OverlayMarqueeTextPositionEnum.center,
          }),
        }),
      },

      {
        title: "Song Title",
        labelKey: "overlayTypes.songTitle",
        icon: <IoMdMusicalNotes />,
        // icon: <AlignLeftOutlined />,
        type: OVERLAY_TYPE.SONG_TITLE,
        getTemplate: () => ({
          id: uuid(),
          type: "song_title",
          text: "Song Title",
          fontSize: 15,
          fill: OVERLAY_WHITE,
          top: 0,
          left: 0,
          settings: JSON.stringify({
            centralizedText: false,
            strokeTextStrokeEnabled: false,
            strokeTextFillType: OverlayStrokeTextFillTypeEnum.solid,
            strokeTextFillColorStops: [0, OVERLAY_WHITE],
            textFontFamily: "Arial",
          }),
        }),
      },
      {
        title: "Song Author",
        labelKey: "overlayTypes.songAuthor",
        icon: <BiPencil />,
        // icon: <AlignLeftOutlined />,
        type: OVERLAY_TYPE.SONG_AUTHOR,
        getTemplate: () => ({
          id: uuid(),
          type: "song_author",
          text: "Song Author",
          fontSize: 15,
          fill: OVERLAY_WHITE,
          top: 0,
          left: 0,
          settings: JSON.stringify({
            centralizedText: false,
            strokeTextStrokeEnabled: false,
            strokeTextFillType: OverlayStrokeTextFillTypeEnum.solid,
            strokeTextFillColorStops: [0, OVERLAY_WHITE],
            textFontFamily: "Arial",
          }),
        }),
      },
      {
        title: "Song Title (file metadata)",
        labelKey: "overlayTypes.songTitleMetadata",
        icon: <IoMdMusicalNotes />,
        // icon: <AlignLeftOutlined />,
        type: OVERLAY_TYPE.SONG_TITLE_METADATA,
        getTemplate: () => ({
          id: uuid(),
          type: "song_title_metadata",
          text: "Song Title (metadata)",
          fontSize: 15,
          fill: OVERLAY_WHITE,
          top: 0,
          left: 0,
          settings: JSON.stringify({
            centralizedText: false,
            strokeTextStrokeEnabled: false,
            strokeTextFillType: OverlayStrokeTextFillTypeEnum.solid,
            strokeTextFillColorStops: [0, OVERLAY_WHITE],
            textFontFamily: "Arial",
          }),
        }),
      },
      {
        title: "Song Author (file metadata)",
        labelKey: "overlayTypes.songAuthorMetadata",
        icon: <BiPencil />,
        // icon: <AlignLeftOutlined />,
        type: OVERLAY_TYPE.SONG_AUTHOR_METADATA,
        getTemplate: () => ({
          id: uuid(),
          type: "song_author_metadata",
          text: "Song Author (metadata)",
          fontSize: 15,
          fill: OVERLAY_WHITE,
          top: 0,
          left: 0,
          settings: JSON.stringify({
            centralizedText: false,
            strokeTextStrokeEnabled: false,
            strokeTextFillType: OverlayStrokeTextFillTypeEnum.solid,
            strokeTextFillColorStops: [0, OVERLAY_WHITE],
            textFontFamily: "Arial",
          }),
        }),
      },
      {
        title: "Song Album (file metadata)",
        labelKey: "overlayTypes.songAlbumMetadata",
        icon: <MdAlbum />,
        // icon: <AlignLeftOutlined />,
        type: OVERLAY_TYPE.SONG_ALBUM_METADATA,
        getTemplate: () => ({
          id: uuid(),
          type: "song_album_metadata",
          text: "Song Album (metadata)",
          fontSize: 15,
          fill: OVERLAY_WHITE,
          top: 0,
          left: 0,
          settings: JSON.stringify({
            centralizedText: false,
            strokeTextStrokeEnabled: false,
            strokeTextFillType: OverlayStrokeTextFillTypeEnum.solid,
            strokeTextFillColorStops: [0, OVERLAY_WHITE],
            textFontFamily: "Arial",
          }),
        }),
      },
    ],
  },
  {
    title: "Media",
    overlays_options: [
      {
        title: "Image",
        labelKey: "overlayTypes.image",
        icon: <MdImage />,
        // icon: <FileImageOutlined />,
        type: OVERLAY_TYPE.IMAGE,
        getTemplate: () => ({
          id: uuid(),
          type: "image",
          top: 0,
          left: 0,
          width: 20,
          height: 20,
          settings: JSON.stringify({
            url: "",
          }),
        }),
      },
      {
        title: "GIF",
        labelKey: "overlayTypes.gif",
        icon: <MdGif />,
        // icon: <FileGifOutlined />,
        type: OVERLAY_TYPE.GIF,
        getTemplate: () => ({
          id: uuid(),
          type: "gif",
          top: 0,
          left: 0,
          width: 20,
          height: 20,
          settings: JSON.stringify({
            url: "",
          }),
        }),
      },
      {
        title: "Video",
        labelKey: "overlayTypes.video",
        icon: <MdVideoLibrary />,
        // icon: <VideoCameraOutlined />,
        type: OVERLAY_TYPE.VIDEO,
        getTemplate: () => ({
          id: uuid(),
          type: "video",
          top: 0,
          left: 0,
          width: 20,
          height: 20,
          settings: JSON.stringify({
            url: "",
          }),
        }),
      },
    ],
  },
  {
    title: "Shapes",
    overlays_options: [
      {
        title: "Circle",
        labelKey: "overlayTypes.circle",
        icon: <FaCircle />,
        // icon: <CopyrightOutlined />,
        type: OVERLAY_TYPE.CIRCLE,
        getTemplate: () => ({
          id: uuid(),
          type: OverlayTypeEnum.circle,
          top: 17.7,
          left: 10,
          width: 20,
          height: 20,
          fill: OVERLAY_WHITE,
          settings: JSON.stringify({
            radius: 10,
          }),
        }),
      },
      {
        title: "Rectangle",
        labelKey: "overlayTypes.rectangle",
        icon: <MdRectangle />,
        // icon: <BorderOutlined />,
        type: OVERLAY_TYPE.RECTANGLE,
        getTemplate: () => ({
          id: uuid(),
          type: OverlayTypeEnum.rectangle,
          top: 0,
          left: 0,
          width: 20,
          height: 20,
          fill: OVERLAY_WHITE,
          settings: JSON.stringify({
            shadowEnabled: false,
          }),
        }),
      },
      {
        title: "Custom Rectangle",
        labelKey: "overlayTypes.customRectangle",
        icon: <TbRectangleRoundedTop />,
        // icon: <RadiusUprightOutlined />,
        type: OVERLAY_TYPE.CUSTOM_RECTANGLE,
        getTemplate: () => ({
          id: uuid(),
          type: OverlayTypeEnum.custom_rectangle,
          top: 0,
          left: 0,
          width: 20,
          height: 20,
          fill: OVERLAY_BLACK,
          settings: JSON.stringify({
            cornerRadius: [15, 15, 15, 15],
          }),
        }),
      },
      {
        title: "Triangle",
        labelKey: "overlayTypes.triangle",
        icon: <IoTriangle />,
        // icon: <BorderOutlined />,
        type: OVERLAY_TYPE.TRIANGLE,
        getTemplate: () => ({
          id: uuid(),
          type: OverlayTypeEnum.triangle,
          top: 10.178997269364697,
          left: 5.027431578161832,
          width: 20,
          height: 20,
          fill: OVERLAY_WHITE,
          settings: JSON.stringify({
            stroke: OVERLAY_BLACK,
            strokeWidth: 2,
          }),
        }),
      },
      {
        title: "Ellipse",
        labelKey: "overlayTypes.ellipse",
        icon: <TfiEye />,
        // icon: <CiCircleOutlined />,
        type: OVERLAY_TYPE.ELLIPSE,
        getTemplate: () => ({
          id: uuid(),
          type: OverlayTypeEnum.ellipse,
          top: 10.4,
          left: 20.2,
          width: 40,
          height: 20,
          fill: OVERLAY_WHITE,
          settings: JSON.stringify({
            radiusX: 20,
            radiusY: 10,
            stroke: OVERLAY_BLACK,
            strokeWidth: 4,
          }),
        }),
      },
      {
        title: "Ring",
        labelKey: "overlayTypes.ring",
        icon: <BsCircle />,
        // icon: <CopyrightOutlined />,
        type: OVERLAY_TYPE.RING,
        getTemplate: () => ({
          id: uuid(),
          type: OverlayTypeEnum.ring,
          top: 36.6,
          left: 20.6,
          width: 40,
          height: 40,
          fill: OVERLAY_WHITE,
          settings: JSON.stringify({
            innerRadius: 15,
            outerRadius: 20,
            stroke: OVERLAY_BLACK,
            strokeWidth: 10,
          }),
        }),
      },
      {
        title: "Arrow",
        labelKey: "overlayTypes.arrow",
        icon: <CgArrowTopRight />,
        // icon: <ArrowsAltOutlined />,
        type: OVERLAY_TYPE.ARROW,
        getTemplate: () => ({
          id: uuid(),
          type: OverlayTypeEnum.arrow,
          top: 0,
          left: 0,
          fill: OVERLAY_WHITE,
          settings: JSON.stringify({
            points: [0, 0, 20, 20],
            stroke: OVERLAY_WHITE,
            strokeWidth: 2,
            pointerLength: 20,
            pointerWidth: 20,
          }),
        }),
      },
    ],
  },
  {
    title: "Custom Elements",
    overlays_options: [
      {
        title: "Audio Visualizer",
        labelKey: "overlayTypes.audioVisualizer",
        icon: <SiAudiomack />,
        type: OVERLAY_TYPE.AUDIO_VISUALIZER,
        getTemplate: () => ({
          id: uuid(),
          type: OverlayTypeEnum.audio_visualizer,
          top: 80,
          left: 25,
          width: 50,
          height: 15,
          fill: "transparent",
          settings: JSON.stringify({
            visualizerSongURL: "/audio/lonely.mp3",
            // "https://hhk.awamedia.store/uploads/new/music/2024/09/Kendrick-Lamar-Not-Like-Us-drake-Diss-(HipHopKit.com).mp3",
            visualizerSongCurrentTime: 0,
            visualizerShapeType: OverlayVisualizerShapeTypeEnum.bars,
            visualizerParticlesCenterShapeType:
              OverlayVisualizerParticlesCenterShapeTypeEnum.image,
            particlesCenterShapeRadius: 1.5,
            particlesCenterShapeSize: 10,
            particlesCircleMinSize: 0.1,
            particlesCircleRangeSize: 5,
            shapeColor: OVERLAY_WHITE,
            fftSize: 128,
            shapeScale: 1,
            visualizerLineWidth: 1,
            visualizerBarsGap: 3,
            maxParticlesNumber: 500,
            particlesEmissionRateNumber: 100,
            sunburstCenterCircleRadius: 10,
            bottomShapeHeight: 20,
            particlesShapeImage: {
              width: 20,
              height: 20,
            },
          }),
        }),
      },
      {
        title: "Countdown",
        labelKey: "overlayTypes.countDown",
        icon: <PiClockCountdown />,
        // icon: <FieldTimeOutlined />,
        type: OVERLAY_TYPE.COUNTDOWN,
        getTemplate: () => ({
          id: uuid(),
          type: OverlayTypeEnum.countdown,
          fill: OVERLAY_WHITE,
          text: "",
          fontSize: 15,
          top: 0,
          left: 0,
          bold: true,
          settings: JSON.stringify({
            countdownStyle: OverlayCountdownStyleEnum.military,
            countdownFormat: [
              OverlayCountdownFormatEnum.days,
              OverlayCountdownFormatEnum.hours,
              OverlayCountdownFormatEnum.minutes,
              OverlayCountdownFormatEnum.seconds,
            ],
            hideWhenFinished: true,
            countdownDate: new Date(
              Date.now() + 1.5 * 24 * 60 * 60 * 1000
            ).toISOString(),
          }),
        }),
      },
      {
        title: "Digital Clock",
        labelKey: "overlayTypes.digitalClock",
        icon: <TbClock12 />,
        // icon: <FieldBinaryOutlined />,
        type: OVERLAY_TYPE.DIGITAL_CLOCK,
        getTemplate: () => ({
          id: uuid(),
          type: OverlayTypeEnum.digital_clock,
          text: "",
          fontSize: 15,
          fill: OVERLAY_WHITE,
          top: 0,
          left: 0,
          bold: true,
          settings: JSON.stringify({
            clockFormat: OverlayClockFormatEnum["12-hour"],
            digitalClockDateEnabled: true,
            digitalClockDateShowDay: true,
            digitalClockDateTextBold: true,
            digitalClockDateTextColor: OVERLAY_WHITE,
            digitalClockDateFontSize: 4,
          }),
        }),
      },
      {
        title: "Analog Clock",
        labelKey: "overlayTypes.analogClock",
        icon: <IoMdClock />,
        // icon: <ClockCircleOutlined />,
        type: OVERLAY_TYPE.ANALOG_CLOCK,
        getTemplate: () => ({
          id: uuid(),
          type: OverlayTypeEnum.analog_clock,
          text: "",
          fontSize: 7,
          fill: OVERLAY_WHITE,
          bold: true,
          left: 12.173582239613054,
          top: 21.64192398153432,
          settings: JSON.stringify({
            analogClockRadius: 12,
            analogClockCenterCircleRadius: 0.5,
            stroke: OVERLAY_WHITE,
            strokeWidth: 2,
            analogClockMinuteMarkersEnabled: true,
            analogClockHoursNumbersEnabled: true,
            analogClockHoursNumbersType:
              OverlayAnalogClockHoursNumbersTypeEnum.arabic,
            analogClockHoursNumbersBold: true,
            analogClockHoursNumbersFill: OVERLAY_BLACK,
            analogClockHoursFill: OVERLAY_BLACK,
            analogClockMinutesFill: OVERLAY_BLACK,
            analogClockSecondsFill: OVERLAY_RED,
            analogClockCenterCircleFill: OVERLAY_BLACK,
            analogClockMinuteMarkersFill: OVERLAY_BLACK,
            analogClockShape: OverlayAnalogClockShapeEnum.circle,
            analogClockSquareRadius: 15,
          }),
        }),
      },
    ],
  },
];

// define several math function
export function getCorner(
  pivotX: number,
  pivotY: number,
  diffX: number,
  diffY: number,
  angle: number
) {
  const distance = Math.sqrt(diffX * diffX + diffY * diffY);

  /// find angle from pivot to corner
  angle += Math.atan2(diffY, diffX);

  /// get new x and y and round it off to integer
  const x = pivotX + distance * Math.cos(angle);
  const y = pivotY + distance * Math.sin(angle);

  return { x: x, y: y };
}
export function getClientRect(rotatedBox: any) {
  const { x, y, width, height } = rotatedBox;
  const rad = rotatedBox.rotation;

  const p1 = getCorner(x, y, 0, 0, rad);
  const p2 = getCorner(x, y, width, 0, rad);
  const p3 = getCorner(x, y, width, height, rad);
  const p4 = getCorner(x, y, 0, height, rad);

  const minX = Math.min(p1.x, p2.x, p3.x, p4.x);
  const minY = Math.min(p1.y, p2.y, p3.y, p4.y);
  const maxX = Math.max(p1.x, p2.x, p3.x, p4.x);
  const maxY = Math.max(p1.y, p2.y, p3.y, p4.y);

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

export function getTotalBox(boxes: any) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  boxes.forEach((box: any) => {
    minX = Math.min(minX, box.x);
    minY = Math.min(minY, box.y);
    maxX = Math.max(maxX, box.x + box.width);
    maxY = Math.max(maxY, box.y + box.height);
  });
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

export function filterOverlays(overlays: IOverlay[]) {
  if (!overlays || !overlays.length) {
    return [];
  }

  return overlays.filter((overlay: any) => {
    if (
      ["gif", "image", "video"].includes(overlay.type) &&
      !overlay.visualMediaId
    ) {
      return false;
    }
    return true;
  });
}
